import React, { useState } from 'react';
import { Row, Section } from '../settingsPrimitives';
import { authService } from '../../services/apiService';
import { collectLocalData } from '../../plans/localDataExport';
import { isTauri } from '../../utils/isTauri';

/**
 * Settings → Privacy → Data export (card 3.7, owner 10.10.2026: "the GDPR
 * data export has to be toggleable — what the user wants to export").
 *
 * One checkbox per category the server knows (`GDPR.export_categories/0`);
 * the account itself always comes along. Everything is ticked except the
 * vault's ciphertext: it is opt-in, unreadable without the master password
 * (the server never had it) and can be large.
 */
export const EXPORT_CATEGORIES = [
  'workspaces',
  'teams',
  // The server's synced documents (`GDPR.DocumentsExport`). Plans v2: the
  // export must cover ALL of a person's data — boards and documents kept on
  // this device come from the "Data on this device" row below.
  'documents',
  // Backend #218: boards, calendar, projects and links (synced records), chat
  // messages, and uploaded files.
  'records',
  'chat_messages',
  'files',
  'request_history',
  'login_history',
  'newsletter',
  'licences',
  'vault_activity',
  'vault_ciphertext',
  'identity',
];
export const DEFAULT_OFF = ['vault_ciphertext'];

function download(data, filename = 'apiboo-export.json') {
  const json = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DataExportSection = ({ t }) => {
  const [chosen, setChosen] = useState(() => EXPORT_CATEGORIES.filter((c) => !DEFAULT_OFF.includes(c)));
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (c) => setChosen((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      // Display order, so the file and the request read the same way.
      const include = EXPORT_CATEGORIES.filter((c) => chosen.includes(c));
      const resp = await authService.exportData(include);
      download(resp?.data ?? resp);
    } catch (err) {
      setError(err.message || t('settings.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  // Boards, documents, calendar and projects kept on THIS device (Free and
  // Basic keep everything here), which no server export can see.
  const handleDeviceExport = async () => {
    setExporting(true);
    setError(null);
    try {
      download(await collectLocalData(), 'apiboo-device-export.json');
    } catch (err) {
      setError(err.message || t('settings.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Section title={t('settings.dataExport')}>
      <Row label={t('settings.exportMyData')} desc={t('settings.dataExportDesc')}>
        <button type="button" className="settings-action-btn flex-shrink-0" onClick={handleExport} disabled={exporting}>
          {exporting ? t('settings.exporting') : t('settings.exportMyData')}
        </button>
      </Row>
      {/* Backend #218: the export carries request content, auth and
          environment values in plain text. */}
      <p className="data-export-note" role="note" data-testid="data-export-secrets">{t('settings.exportSecretsNote')}</p>
      <fieldset className="data-export-choices" disabled={exporting}>
        <legend className="data-export-legend">{t('settings.exportChoose')}</legend>
        {EXPORT_CATEGORIES.map((c) => (
          <label key={c} className="data-export-choice">
            <input type="checkbox" className="checkbox-brand" checked={chosen.includes(c)} onChange={() => toggle(c)} />
            <span className="data-export-choice-text">
              <span className="data-export-choice-name">{t(`settings.exportCat.${c}`)}</span>
              <span className="data-export-choice-desc">{t(`settings.exportCatDesc.${c}`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <p className="data-export-note">{t('settings.exportBackupsNote')}</p>
      {isTauri() && (
        <Row label={t('settings.deviceExport')} desc={t('settings.deviceExportDesc')}>
          <button type="button" className="settings-action-btn flex-shrink-0" onClick={handleDeviceExport} disabled={exporting}>
            {t('settings.deviceExportBtn')}
          </button>
        </Row>
      )}
      {error && (
        <div className="privacy-feedback privacy-feedback--error" style={{ marginTop: 6 }}>
          {error}
        </div>
      )}
    </Section>
  );
};

export default DataExportSection;
